import dynamic from 'next/dynamic';

import classNames from 'classnames';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useIsClient } from 'usehooks-ts';

import ImageProxy from '@next-image-proxy';

import ExternalLink from '@uikit/components/ExternalLink/ExternalLink';

import { getMixPanelPage, MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import { useUserAdSettings } from '@shared/common/components/NavLayout/AdBlocks';
import { useMixPanel } from '@shared/common/providers/MixPanelProvider';

import { useGetBannersQuery } from '@query/launchpad';

const LaunchpadMainInlineMiddleBanner = dynamic(
  () => import('@shared/common/components/NavLayout/AdBlocks/LaunchpadMainInlineMiddleBanner'),
  {
    ssr: false,
  },
);

interface MainAdBannerProps {
  userId?: number;
}

export const MainAdBanner = ({ userId }: MainAdBannerProps) => {
  const { showLaunchpadMainInlineMiddleBanner } = useUserAdSettings();
  const { data } = useGetBannersQuery();
  const { track } = useMixPanel();
  const page = getMixPanelPage();
  const isClient = useIsClient();

  const trackBannerClick = () => {
    track(MIXPANEL_EVENT_NAMES.BANNER_CLICK, {
      distinct_id: userId,
      page,
      banner: 'Magic Store Big App Banner',
    });
  };

  const hasData = data && data.length > 0;

  if (!hasData && !showLaunchpadMainInlineMiddleBanner) {
    return null;
  }

  if (!isClient) {
    return <div className="skeleton-block aspect-[1920/540] w-full h-full" />;
  }

  return (
    <div className="w-full  static aspect-[1920/540] my-4 mb-6 md:my-12">
      {showLaunchpadMainInlineMiddleBanner ? (
        <LaunchpadMainInlineMiddleBanner userId={userId} />
      ) : (
        <Swiper
          onClick={trackBannerClick}
          loop
          grabCursor
          spaceBetween={16}
          slidesPerView={1}
          modules={[Autoplay, Pagination]}
          autoplay={{
            delay: 5000,
            pauseOnMouseEnter: true,
          }}
          pagination={{
            el: '.launchpad-main-page-banners-swiper-pagination',
            type: 'bullets',
            clickable: true,
            bulletClass:
              'block w-3 h-3 rounded-full aspect-square bg-primary-300 hover:cursor-pointer',
            bulletActiveClass: 'bg-primary-700',
          }}
        >
          <div className="launchpad-main-page-banners-swiper-pagination w-full flex justify-center items-center gap-2 mt-4 sm:mt-6"></div>

          {data &&
            data.map((banner) => {
              return (
                <SwiperSlide key={banner.id} className={classNames('select-none w-full')}>
                  <ExternalLink href={banner?.link} className="block">
                    <ImageProxy
                      src={banner?.desktopImage}
                      alt={banner?.altText}
                      className={classNames(
                        'w-full object-cover flex justify-center items-center overflow-hidden rounded-lg aspect-[1920/540]',
                      )}
                      width={1920}
                      height={540}
                    />
                  </ExternalLink>
                </SwiperSlide>
              );
            })}
        </Swiper>
      )}
    </div>
  );
};
